import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import type { LifeStatus } from '@/api/type/user';
import type { Member } from '@/types';
import { EDUCATION_QUALIFICATION_OPTIONS } from '@/lib/constants';
import { PLAN_MONTHS } from '@/lib/payments';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const getEducationQualificationLabel = (value?: string | null) =>
  EDUCATION_QUALIFICATION_OPTIONS.find((option) => option.label.toLowerCase() === value?.trim().toLowerCase())?.label ??
  (value?.trim() || undefined);

export function formatFileName(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  const hasExtension = lastDot > 0;
  const baseName = hasExtension ? filename.slice(0, lastDot) : filename;
  const extension = hasExtension ? filename.slice(lastDot) : '';

  if (baseName.length <= 15) {
    return filename;
  }

  return `${baseName.slice(0, 10)}.....${baseName.slice(-5)}${extension}`;
}

export const getLifeStatus = (member: Pick<Member, 'lifeStatus'>): LifeStatus =>
  member.lifeStatus === 'deceased' ? 'deceased' : 'alive';

export type MembershipStatus = 'active' | 'completed' | 'closed';

export const getMembershipStatus = (
  member: Pick<Member, 'lifeStatus'>,
  monthsPaid: number
): MembershipStatus => {
  if (getLifeStatus(member) === 'deceased') return 'closed';
  if (monthsPaid >= PLAN_MONTHS) return 'completed';
  return 'active';
};

export type PaymentMonthStatus = 'paid' | 'unpaid' | 'closed' | 'no_dues';

export const getPaymentMonthStatus = (
  member: Pick<Member, 'lifeStatus'>,
  isPaidThisMonth: boolean,
  monthsPaid: number
): PaymentMonthStatus => {
  if (getLifeStatus(member) === 'deceased') return 'closed';
  if (monthsPaid >= PLAN_MONTHS) return 'no_dues';
  return isPaidThisMonth ? 'paid' : 'unpaid';
};

export const SUCCESS_NOTICE_CLASS =
  'rounded-lg border border-teal-100/90 bg-teal-50/50 px-4 py-3 text-sm text-gray-700';

export const SUCCESS_NOTICE_ACCENT_CLASS = 'text-teal-800';

export const SUCCESS_AMOUNT_CLASS = 'font-semibold tabular-nums text-teal-800';

export const isPaymentClosed = (member: Pick<Member, 'lifeStatus'>): boolean =>
  getLifeStatus(member) === 'deceased';

export const isAddPaymentDisabled = (
  member: Pick<Member, 'lifeStatus'>,
  monthsPaid: number
): boolean => {
  const status = getMembershipStatus(member, monthsPaid);
  return status === 'closed' || status === 'completed';
};
